/**
 * IdleDetector
 * Detects user inactivity and auto-switches to Away status
 * Matches the test app's idle detection implementation
 */
import { PresenceStatus } from './types';
import { ConnectionState } from '../shared/types';
export class IdleDetector {
    /**
     * Create IdleDetector
     * @param presenceClient - PresenceClient instance
     * @param activityTracker - ActivityTracker instance to monitor activity
     * @param idleTimeoutMinutes - Minutes of inactivity before going idle (default: 5)
     * @param gracePeriodSeconds - Grace period in seconds before marking idle (default: 30)
     */
    constructor(presenceClient, activityTracker, idleTimeoutMinutes = 5, gracePeriodSeconds = 30) {
        this.presenceClient = presenceClient;
        this.activityTracker = activityTracker;
        this.idleTimeoutMinutes = idleTimeoutMinutes;
        this.gracePeriodSeconds = gracePeriodSeconds;
        this.checkInterval = null;
        this.isIdle = false;
        this.isRunning = false;
        this.idleCallback = null;
        this.previousStatus = null;
        this.stateChangeUnsubscribe = null;
        this.wasPausedDueToDisconnect = false;
        // Listen for connection state changes
        this.stateChangeUnsubscribe = this.presenceClient.onStateChange((state) => {
            this.handleConnectionStateChange(state);
        });
    }
    /**
     * Start idle detection
     * Begins monitoring activity and auto-switching to Away when idle
     * @param onIdleChange - Optional callback when idle state changes
     */
    start(onIdleChange) {
        if (this.isRunning) {
            console.warn('[IdleDetector] Already running');
            return;
        }
        console.log(`[IdleDetector] Starting with ${this.idleTimeoutMinutes} minute timeout`);
        this.idleCallback = onIdleChange || null;
        // Check for idleness every second
        this.checkInterval = setInterval(() => {
            this.checkIdleStatus();
        }, 1000);
        this.isRunning = true;
    }
    /**
     * Stop idle detection
     */
    stop() {
        if (this.checkInterval) {
            clearInterval(this.checkInterval);
            this.checkInterval = null;
        }
        this.isRunning = false;
        this.isIdle = false;
        console.log('[IdleDetector] Stopped');
    }
    /**
     * Handle connection state changes
     * Pauses idle detection when disconnected/reconnecting, resumes when connected
     * @private
     */
    handleConnectionStateChange(state) {
        if (state === ConnectionState.Disconnected || state === ConnectionState.Reconnecting) {
            // Pause if running
            if (this.isRunning) {
                console.log(`[IdleDetector] Pausing due to connection state: ${state}`);
                if (this.checkInterval) {
                    clearInterval(this.checkInterval);
                    this.checkInterval = null;
                }
                this.wasPausedDueToDisconnect = true;
            }
        }
        else if (state === ConnectionState.Connected) {
            // Resume if was paused due to disconnect
            if (this.wasPausedDueToDisconnect && this.isRunning) {
                console.log('[IdleDetector] Resuming after reconnection');
                this.checkInterval = setInterval(() => {
                    this.checkIdleStatus();
                }, 1000);
                this.wasPausedDueToDisconnect = false;
            }
        }
    }
    /**
     * Check current idle status and trigger state changes
     * @private
     */
    checkIdleStatus() {
        const now = Date.now();
        const lastActivity = this.activityTracker.getLastActivityTime();
        const idleThresholdMs = this.idleTimeoutMinutes * 60 * 1000;
        const gracePeriodMs = this.gracePeriodSeconds * 1000;
        const timeSinceActivity = now - lastActivity;
        // Check if we should transition to idle (with grace period)
        if (timeSinceActivity >= (idleThresholdMs + gracePeriodMs) && !this.isIdle) {
            console.log(`[IdleDetector] User idle after ${timeSinceActivity}ms (threshold: ${idleThresholdMs}ms + grace: ${gracePeriodMs}ms)`);
            this.setIdle(true);
        }
        // Check if we should transition back from idle
        else if (timeSinceActivity < idleThresholdMs && this.isIdle) {
            console.log(`[IdleDetector] User active again after ${timeSinceActivity}ms`);
            this.setIdle(false);
        }
    }
    /**
     * Set idle state and update presence status
     * @param idle - True to set idle, false to clear idle
     * @private
     */
    async setIdle(idle) {
        this.isIdle = idle;
        console.log(`[IdleDetector] Idle state changed: ${idle}`);
        // Notify callback if provided
        if (this.idleCallback) {
            try {
                this.idleCallback(idle);
            }
            catch (error) {
                console.error('[IdleDetector] Error in idle callback:', error);
            }
        }
        // Handle status change
        if (idle) {
            // Going idle - switch to Away
            try {
                // Check if connected before invoking hub method
                if (!this.presenceClient.isConnected()) {
                    console.warn('[IdleDetector] Not connected, skipping status update');
                    return;
                }
                // Store current status before going idle
                const currentPresence = this.presenceClient.getCurrentUserPresence();
                if (currentPresence) {
                    this.previousStatus = currentPresence.status;
                }
                // Switch to Away with idle message
                await this.presenceClient.updateStatus(PresenceStatus.Away, 'Away - Idle');
                console.log('[IdleDetector] Auto-switched to Away status');
            }
            catch (error) {
                console.error('[IdleDetector] Failed to set Away status:', error);
            }
        }
        else {
            // Returning from idle
            console.log('[IdleDetector] User is active again');
            // Optionally restore previous status
            // For now, we let the activity tracker handle status updates
            // The backend's RecordActivity will auto-switch Away → Online
        }
    }
    /**
     * Get current idle state
     * @returns True if currently idle, false otherwise
     */
    getIdleStatus() {
        return this.isIdle;
    }
    /**
     * Get remaining time before going idle
     * @returns Remaining seconds until idle, or 0 if already idle
     */
    getRemainingTime() {
        if (this.isIdle) {
            return 0;
        }
        const now = Date.now();
        const lastActivity = this.activityTracker.getLastActivityTime();
        const idleThresholdMs = this.idleTimeoutMinutes * 60 * 1000;
        const gracePeriodMs = this.gracePeriodSeconds * 1000;
        const totalThresholdMs = idleThresholdMs + gracePeriodMs;
        const remainingMs = totalThresholdMs - (now - lastActivity);
        return Math.max(0, Math.ceil(remainingMs / 1000)); // Return seconds
    }
    /**
     * Get formatted remaining time string (e.g., "4m 32s")
     * @returns Formatted time string
     */
    getFormattedRemainingTime() {
        const totalSeconds = this.getRemainingTime();
        if (totalSeconds === 0) {
            return 'Idle';
        }
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;
        if (minutes > 0) {
            return `${minutes}m ${seconds}s`;
        }
        return `${seconds}s`;
    }
    /**
     * Update the idle timeout
     * @param minutes - New timeout in minutes
     */
    setIdleTimeout(minutes) {
        if (minutes < 1) {
            console.warn('[IdleDetector] Timeout too short, using minimum 1 minute');
            minutes = 1;
        }
        if (minutes > 60) {
            console.warn('[IdleDetector] Timeout too long, using maximum 60 minutes');
            minutes = 60;
        }
        this.idleTimeoutMinutes = minutes;
        console.log(`[IdleDetector] Timeout updated to ${minutes} minutes`);
    }
    /**
     * Get current timeout setting
     * @returns Timeout in minutes
     */
    getIdleTimeout() {
        return this.idleTimeoutMinutes;
    }
    /**
     * Check if idle detection is currently running
     * @returns True if running, false otherwise
     */
    isActive() {
        return this.isRunning;
    }
    /**
     * Manually force idle state
     * @param idle - True to force idle, false to force active
     */
    async forceIdleState(idle) {
        await this.setIdle(idle);
    }
    /**
     * Cleanup and destroy the idle detector
     * Stops detection and unsubscribes from connection state changes
     */
    destroy() {
        this.stop();
        if (this.stateChangeUnsubscribe) {
            this.stateChangeUnsubscribe();
            this.stateChangeUnsubscribe = null;
        }
    }
    /**
     * Update the grace period
     * @param seconds - New grace period in seconds
     */
    setGracePeriod(seconds) {
        if (seconds < 0) {
            console.warn('[IdleDetector] Grace period cannot be negative, using 0');
            seconds = 0;
        }
        if (seconds > 120) {
            console.warn('[IdleDetector] Grace period too long, using maximum 120 seconds');
            seconds = 120;
        }
        this.gracePeriodSeconds = seconds;
        console.log(`[IdleDetector] Grace period updated to ${seconds} seconds`);
    }
    /**
     * Get current grace period setting
     * @returns Grace period in seconds
     */
    getGracePeriod() {
        return this.gracePeriodSeconds;
    }
}
//# sourceMappingURL=IdleDetector.js.map